import { experience, ExtraParamsKey, type RequestErrorBody } from '@logto/schemas';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { checkOneTimeToken } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import LoadingLayer from '@/shared/components/LoadingLayer';
import { searchKeys } from '@/shared/utils/search-parameters';

import { getOneTimeTokenClientId } from './can-refresh-one-time-token.js';

type OneTimeTokenErrorState = {
  readonly errorCode?: string;
  readonly errorMessage?: string;
  /** Resolved from the token row when check fails; used for renew without login_hint. */
  readonly email?: string;
};

const getEmailFromErrorData = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null || !('email' in data)) {
    return;
  }

  return typeof data.email === 'string' ? data.email : undefined;
};

/**
 * DXM magic-link gate: validate + consume the token, then redirect to the application's
 * registered redirect URI — or show error/renew. No password or OIDC login UI.
 *
 * Selected when `NEW_OTT_FEATURE_FLAG` is on and `source` is not a classic flow value.
 * Needs `one_time_token` + `app_id`; email comes from the token row (`login_hint` is optional).
 */
const MagicLinkGate = () => {
  const [params] = useSearchParams();
  const navigate = useNavigateWithPreservedSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const isSubmitted = useRef(false);

  const asyncCheckOneTimeToken = useApi(checkOneTimeToken);
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();

  const goToErrorPage = useCallback(
    (state?: OneTimeTokenErrorState) => {
      const next = new URLSearchParams(params);
      // Keep error fields in the query so renew UI survives refresh (state alone does not).
      if (state?.errorCode) {
        next.set('errorCode', state.errorCode);
      } else {
        next.delete('errorCode');
      }
      if (state?.errorMessage) {
        next.set('errorMessage', state.errorMessage);
      } else {
        next.delete('errorMessage');
      }
      if (state?.email) {
        next.set(ExtraParamsKey.LoginHint, state.email);
      }
      navigate(
        {
          pathname: `/${experience.routes.oneTimeToken}/error`,
          search: next.size > 0 ? `?${next.toString()}` : '',
        },
        { replace: true, state }
      );
    },
    [navigate, params]
  );

  useEffect(() => {
    (async () => {
      const token = params.get(ExtraParamsKey.OneTimeToken);
      const clientId = getOneTimeTokenClientId(
        params,
        sessionStorage.getItem(searchKeys.appId) ?? undefined
      );

      if (!token || !clientId) {
        goToErrorPage();
        return;
      }

      if (isSubmitted.current) {
        return;
      }
      // eslint-disable-next-line @silverhand/fp/no-mutation
      isSubmitted.current = true;

      setIsLoading(true);
      const [error, result] = await asyncCheckOneTimeToken({ token, clientId });

      if (error) {
        setIsLoading(false);
        await handleError(error, {
          global: (requestError: RequestErrorBody) => {
            goToErrorPage({
              errorCode: requestError.code,
              errorMessage: requestError.message,
              email:
                getEmailFromErrorData(requestError.data) ??
                params.get(ExtraParamsKey.LoginHint) ??
                undefined,
            });
          },
        });
        return;
      }

      if (!result?.redirectUri) {
        setIsLoading(false);
        goToErrorPage();
        return;
      }

      await redirectTo(result.redirectUri);
    })();
  }, [asyncCheckOneTimeToken, goToErrorPage, handleError, navigate, params, redirectTo]);

  return isLoading ? <LoadingLayer /> : null;
};

export default MagicLinkGate;
